/**
 * Lead source tracking utility
 * Captures and stores UTM parameters, referrer, and landing page for HubSpot integration
 */

export interface LeadSourceData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
}

const STORAGE_KEY = 'hubspot_lead_source';
const LANDING_PAGE_KEY = 'hubspot_landing_page';

/**
 * Extract UTM parameters from current URL
 */
export function captureLeadSource(): LeadSourceData {
  if (typeof window === 'undefined') {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  const leadSource: LeadSourceData = {};

  // Extract UTM parameters
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const utmTerm = urlParams.get('utm_term');
  const utmContent = urlParams.get('utm_content');

  if (utmSource) leadSource.utm_source = utmSource;
  if (utmMedium) leadSource.utm_medium = utmMedium;
  if (utmCampaign) leadSource.utm_campaign = utmCampaign;
  if (utmTerm) leadSource.utm_term = utmTerm;
  if (utmContent) leadSource.utm_content = utmContent;

  // Capture referrer (if not already stored)
  if (document.referrer && !leadSource.referrer) {
    leadSource.referrer = document.referrer;
  }

  // Capture landing page (first page visited in this session)
  const storedLandingPage = sessionStorage.getItem(LANDING_PAGE_KEY);
  if (!storedLandingPage) {
    const landingPage = window.location.href;
    leadSource.landing_page = landingPage;
    sessionStorage.setItem(LANDING_PAGE_KEY, landingPage);
  } else {
    leadSource.landing_page = storedLandingPage;
  }

  return leadSource;
}

/**
 * Store lead source data in sessionStorage
 * Merges with existing data to preserve UTM parameters throughout the session
 */
export function storeLeadSource(data: LeadSourceData): void {
  if (typeof window === 'undefined') return;

  // Get existing data
  const existing = getStoredLeadSource();

  // Merge with new data (new data takes precedence for UTM params, but preserve referrer/landing_page)
  const merged: LeadSourceData = {
    ...existing,
    ...data,
    // Preserve landing page if already set
    landing_page: existing.landing_page || data.landing_page,
    // Preserve referrer if already set
    referrer: existing.referrer || data.referrer,
  };

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('Failed to store lead source in sessionStorage:', error);
    }
  }
}

/**
 * Get stored lead source data from sessionStorage
 */
export function getStoredLeadSource(): LeadSourceData {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as LeadSourceData;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('Failed to read lead source from sessionStorage:', error);
    }
  }

  return {};
}

/**
 * Get formatted lead source data for HubSpot form submission
 * Returns data in format expected by HubSpot API
 */
export function getLeadSourceData(): LeadSourceData {
  // Get stored data (which includes UTM params captured on first visit)
  const stored = getStoredLeadSource();

  // If we have stored data, use it
  if (Object.keys(stored).length > 0) {
    return stored;
  }

  // Otherwise, try to capture from current URL
  return captureLeadSource();
}

/**
 * Initialize lead source tracking on app load
 * Should be called once when the app starts
 */
export function initLeadSourceTracking(): void {
  if (typeof window === 'undefined') return;

  // Capture UTM parameters from URL if present
  const leadSource = captureLeadSource();

  // Store in sessionStorage for the entire session
  if (Object.keys(leadSource).length > 0) {
    storeLeadSource(leadSource);
  }

  // Also capture referrer if not already stored
  if (document.referrer && !leadSource.referrer) {
    storeLeadSource({ referrer: document.referrer });
  }
}

/**
 * Get lead source data formatted for HubSpot API
 * Returns an array of field objects ready to include in HubSpot form submission
 */
export function getLeadSourceFields(): Array<{ name: string; value: string }> {
  const data = getLeadSourceData();
  const fields: Array<{ name: string; value: string }> = [];

  // HubSpot standard analytics fields
  if (data.utm_source) {
    fields.push({ name: 'hs_analytics_source', value: data.utm_source });
  }
  if (data.utm_medium) {
    fields.push({ name: 'hs_analytics_medium', value: data.utm_medium });
  }
  if (data.utm_campaign) {
    fields.push({ name: 'hs_analytics_campaign', value: data.utm_campaign });
  }

  // Custom properties (need to be created in HubSpot)
  if (data.landing_page) {
    fields.push({ name: 'landing_page', value: data.landing_page });
  }
  if (data.referrer) {
    fields.push({ name: 'referrer', value: data.referrer });
  }

  return fields;
}

